import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Archive, UserCog, X } from "lucide-react";

// CC-266: bulk Reassign owner / Archive for the selected pipeline rows. Sends no email or Slack.
export const ARCHIVE_REASONS = ["Rejected", "Withdrew", "Duplicate", "Test data", "Not qualified", "Other"];
const NEEDS_NOTES = new Set(["Other", "Not qualified"]);

function ReassignDialog({ ids, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [owner, setOwner] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.active !== false && u.email))).catch(() => setUsers([]));
  }, []);
  async function go() {
    setBusy(true);
    try {
      const { data } = await api.post("/hiring/candidates-bulk/reassign-owner",
        { candidate_ids: ids, new_owner_email: owner, reason: reason.trim() || null });
      const skipped = data.results.filter((r) => !r.ok).length;
      toast.success(`Reassigned ${data.updated} candidate${data.updated === 1 ? "" : "s"} to ${owner}${skipped ? ` · ${skipped} skipped` : ""}`);
      onDone();
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign owner for {ids.length} candidate{ids.length === 1 ? "" : "s"}?</DialogTitle>
          <DialogDescription>Stage and SLA clock stay the same. No email or Slack is sent.</DialogDescription>
        </DialogHeader>
        <select value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full border border-[#d1d5db] rounded px-2 py-1.5 text-sm"
          data-testid="bulk-reassign-owner-select">
          <option value="">Choose new owner…</option>
          {users.map((u) => <option key={u.email} value={u.email}>{u.name ? `${u.name} — ${u.email}` : u.email}</option>)}
        </select>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (optional)"
          className="w-full border border-[#d1d5db] rounded px-2 py-1.5 text-sm" data-testid="bulk-reassign-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-reassign-cancel">Cancel</Button>
          <Button onClick={go} disabled={!owner || busy} data-testid="bulk-reassign-confirm">{busy ? "Reassigning…" : "Confirm reassign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveDialog({ ids, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const notesOk = !NEEDS_NOTES.has(reason) || (reason === "Not qualified" ? notes.trim().length >= 15 : notes.trim().length > 0);
  async function go() {
    setBusy(true);
    try {
      const { data } = await api.post("/hiring/candidates-bulk/archive", { candidate_ids: ids, reason, notes: notes.trim() });
      const skipped = data.results.filter((r) => !r.ok).length;
      toast.success(`Archived ${data.updated} candidate${data.updated === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""}`);
      onDone();
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-archive-dialog">
        <DialogHeader>
          <DialogTitle>Archive {ids.length} candidate{ids.length === 1 ? "" : "s"}?</DialogTitle>
          <DialogDescription>They move to the Archived tab. No email is sent to candidates, including for Not qualified.</DialogDescription>
        </DialogHeader>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-[#d1d5db] rounded px-2 py-1.5 text-sm"
          data-testid="bulk-archive-reason">
          <option value="">Choose a reason…</option>
          {ARCHIVE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          placeholder={reason === "Not qualified" ? "Notes (at least 15 characters)" : NEEDS_NOTES.has(reason) ? "Notes (required)" : "Notes (optional)"}
          className="w-full border border-[#d1d5db] rounded px-2 py-1.5 text-sm" data-testid="bulk-archive-notes" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-archive-cancel">Cancel</Button>
          <Button onClick={go} disabled={!reason || !notesOk || busy} className="bg-[#991b1b] hover:bg-[#7f1d1d]"
            data-testid="bulk-archive-confirm">{busy ? "Archiving…" : "Confirm archive"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PipelineBulkBar({ ids, onClear, onDone }) {
  const [open, setOpen] = useState(null);
  if (!ids.length) return null;
  const done = () => { setOpen(null); onDone(); };
  return (
    <div className="sticky top-2 z-10 bg-[#0e1420] text-white rounded-lg px-4 py-2 flex items-center gap-3 text-sm" data-testid="hiring-bulk-bar">
      <span data-testid="hiring-bulk-count"><strong>{ids.length}</strong> selected</span>
      <Button size="sm" variant="secondary" onClick={() => setOpen("reassign")} data-testid="hiring-bulk-reassign">
        <UserCog className="w-4 h-4 mr-1" /> Reassign owner
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setOpen("archive")} data-testid="hiring-bulk-archive">
        <Archive className="w-4 h-4 mr-1" /> Archive
      </Button>
      <button onClick={onClear} className="ml-auto inline-flex items-center gap-1 text-xs text-[#cbd5e1] hover:text-white" data-testid="hiring-bulk-clear">
        <X className="w-3.5 h-3.5" /> Clear
      </button>
      {open === "reassign" && <ReassignDialog ids={ids} onClose={() => setOpen(null)} onDone={done} />}
      {open === "archive" && <ArchiveDialog ids={ids} onClose={() => setOpen(null)} onDone={done} />}
    </div>
  );
}
